import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import { getFunds, saveBatchFunds } from '../../api/funds';
import { useFundTypes } from '../../hooks/useFundTypes';
import { useCrcies } from '../../hooks/useCrcies';
import {
  useFundClassifications,
  useIsaFundClassifications,
  useProspectusExposureTypes,
  useOperators,
  useTrustees,
  useCompanies,
} from '../../hooks/fundLookups';
import type { MutualFund } from '../../types/MutualFund';
import { t } from '../../i18n/t';
import { formatDateOnly } from '../../utils/dateTime';

type Opt = { id: number; name: string };

// Legacy Etn_static_BO fields (V1.0.10). Order drives the grid column order.
const ETN_FIELDS: (keyof MutualFund)[] = [
  'issuerId', 'issuedQuantity', 'conversionPercent', 'managementFeePercent', 'managementFeeStartDate',
  'includedInIsaReport', 'isActive', 'name4Isa', 'trusteeId', 'leverage', 'fundClassificationId', 'crcyId',
  'createdDate', 'allow2HoldInternal', 'allow2BeInternal', 'isForeign', 'nickName', 'nameEn', 'companyId',
  'magnaNumber', 'lastTrusteePaymentDate', 'trusteeFeePercent', 'dynamicManagementFeePercent', 'isComposite',
  'indexRateDmfFirstDay', 'fundRateDmfFirstDay', 'balanceMfFirstDay', 'balanceTrusteeFeeFirstDay', 'balanceDmfFirstDay',
  'lastValuationAt', 'lastManagementFeePaymentDate', 'dmfStartDate', 'crcyRateDmfFirstDay', 'trusteeFeeStartDate',
  'valuationOrderGroup', 'isPreMarketExchange', 'interestCrcyTicker', 'firstDayIndexCrcyRate', 'isLatePreMarketExchange',
  'sample1730AtDmfStartDate', 'lastTradeDate', 'eodCrcyId', 'isaFundClassificationId', 'isCollateralInCreditLeverage',
  'determiningDate', 'prospectusExposureTypeId', 'operatorId', 'creationSpread', 'redemptionSpread', 'nominalCrcyId',
  'dmfPsagotPlugNumber', 'redemptionLimitsStartDate', 'firstTradeDateNav',
];

const ETN_DATE_FIELDS = new Set<keyof MutualFund>([
  'managementFeeStartDate', 'createdDate', 'lastTrusteePaymentDate', 'lastValuationAt', 'lastManagementFeePaymentDate',
  'dmfStartDate', 'trusteeFeeStartDate', 'lastTradeDate', 'determiningDate', 'redemptionLimitsStartDate',
]);
const ETN_BOOL_FIELDS = new Set<keyof MutualFund>([
  'includedInIsaReport', 'isActive', 'allow2HoldInternal', 'allow2BeInternal', 'isForeign', 'isComposite',
  'isPreMarketExchange', 'isLatePreMarketExchange', 'isCollateralInCreditLeverage',
]);
const ETN_TEXT_FIELDS = new Set<keyof MutualFund>([
  'name4Isa', 'nickName', 'nameEn', 'valuationOrderGroup', 'interestCrcyTicker',
]);
// Small-lookup FK columns → dropdowns. (issuerId stays plain — ISSUERS has 546 rows.)
const ETN_FK_FIELDS = new Set<keyof MutualFund>([
  'fundClassificationId', 'isaFundClassificationId', 'prospectusExposureTypeId', 'operatorId', 'trusteeId',
  'companyId', 'crcyId', 'nominalCrcyId', 'eodCrcyId',
]);

const newFundDefaults = (): Partial<MutualFund> => {
  const base: Record<string, unknown> = {
    fundId: 0,
    fundName: '',
    fundOfficialName: null,
    fundTypeId: 0,
    fundAssetId: 0,
    tradingStartDate: null,
  };
  for (const f of ETN_FIELDS) base[f as string] = null;
  return base as Partial<MutualFund>;
};

function formatDate(value: unknown): string {
  return formatDateOnly(value);
}

export function FundsPage() {
  const fundTypes = useFundTypes();
  const crcies = useCrcies();
  const fundClassifications = useFundClassifications();
  const isaFundClassifications = useIsaFundClassifications();
  const prospectusExposureTypes = useProspectusExposureTypes();
  const operators = useOperators();
  const trustees = useTrustees();
  const companies = useCompanies();

  // Gate only on the CENTRAL lookups. companies is tenant-scoped and can race
  // tenant-init on a hard reload — treat it as best-effort (its dropdown just
  // shows ids until it loads), never blocking/erroring the whole grid.
  const gateLookups = [fundTypes, crcies, fundClassifications, isaFundClassifications, prospectusExposureTypes, operators, trustees];
  const anyError = gateLookups.map((l) => l.error).find(Boolean) ?? null;
  const loading = gateLookups.some((l) => l.loading) || gateLookups.some((l) => !l.data);

  const columnDefs = useMemo<ColDef<MutualFund>[]>(() => {
    if (loading) return [];
    const noneLabel = t('funds.noneOption');

    const crcyOpts: Opt[] = (crcies.data ?? []).map((c) => ({ id: c.crcyId, name: c.crcyName }));
    const fkOpts: Partial<Record<keyof MutualFund, Opt[]>> = {
      fundClassificationId: (fundClassifications.data ?? []).map((x) => ({ id: x.fundClassificationId, name: x.fundClassificationName })),
      isaFundClassificationId: (isaFundClassifications.data ?? []).map((x) => ({ id: x.isaFundClassificationId, name: x.isaFundClassificationName })),
      prospectusExposureTypeId: (prospectusExposureTypes.data ?? []).map((x) => ({ id: x.prospectusExposureTypeId, name: x.prospectusExposureTypeName })),
      operatorId: (operators.data ?? []).map((x) => ({ id: x.operatorId, name: x.operatorName })),
      trusteeId: (trustees.data ?? []).map((x) => ({ id: x.trusteeId, name: x.trusteeName })),
      companyId: (companies.data ?? []).map((x) => ({ id: x.companyId, name: x.companyHebName })),
      crcyId: crcyOpts,
      nominalCrcyId: crcyOpts,
      eodCrcyId: crcyOpts,
    };

    const makeFkCol = (field: keyof MutualFund, headerName: string, opts: Opt[]): ColDef<MutualFund> => {
      const values = [noneLabel, ...opts.map((o) => `${o.id} - ${o.name}`)];
      const labelById = new Map<number, string>(opts.map((o) => [o.id, `${o.id} - ${o.name}`]));
      const idByLabel = new Map<string, number>(opts.map((o) => [`${o.id} - ${o.name}`, o.id]));
      return {
        field,
        headerName,
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values },
        valueGetter: (p: ValueGetterParams<MutualFund>) => {
          const v = p.data?.[field] as number | null | undefined;
          if (v == null) return noneLabel;
          return labelById.get(v) ?? String(v);
        },
        valueSetter: (p: ValueSetterParams<MutualFund>) => {
          if (!p.data) return false;
          const d = p.data as unknown as Record<string, number | null>;
          if (p.newValue === noneLabel) {
            d[field as string] = null;
            return true;
          }
          const id = idByLabel.get(p.newValue);
          if (id === undefined) return false;
          d[field as string] = id;
          return true;
        },
      };
    };

    // fundTypeId dropdown (required, no "none").
    const ftNames = (fundTypes.data ?? []).map((ft) => ft.fundTypeName);
    const ftNameById = new Map<number, string>((fundTypes.data ?? []).map((ft) => [ft.fundTypeId, ft.fundTypeName]));
    const ftIdByName = new Map<string, number>((fundTypes.data ?? []).map((ft) => [ft.fundTypeName, ft.fundTypeId]));

    return [
      {
        field: 'fundId',
        headerName: t('funds.columnFundId'),
        width: 80,
        editable: false,
        sort: 'asc',
        valueFormatter: (p: ValueFormatterParams<MutualFund>) => {
          const isAdded = (p.data as { __status?: string } | undefined)?.__status === 'added';
          if (isAdded) return '';
          return p.value == null ? '' : String(p.value);
        },
      },
      { field: 'fundName', headerName: t('funds.columnFundName'), flex: 1, editable: true },
      { field: 'fundOfficialName', headerName: t('funds.columnFundOfficialName'), flex: 1, editable: true },
      {
        field: 'fundTypeId',
        headerName: t('funds.columnFundType'),
        width: 140,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: ftNames },
        valueGetter: (p: ValueGetterParams<MutualFund>) => (p.data ? (ftNameById.get(p.data.fundTypeId) ?? '') : ''),
        valueSetter: (p: ValueSetterParams<MutualFund>) => {
          if (!p.data) return false;
          const newId = ftIdByName.get(p.newValue);
          if (newId === undefined) return false;
          p.data.fundTypeId = newId;
          return true;
        },
      },
      { field: 'fundAssetId', headerName: t('funds.columnFundAssetId'), width: 140, editable: true, cellDataType: 'number' },
      {
        field: 'tradingStartDate',
        headerName: t('funds.columnTradingStartDate'),
        width: 160,
        editable: true,
        cellDataType: 'date',
        valueFormatter: (p: ValueFormatterParams<MutualFund>) => formatDate(p.value),
      },
      ...ETN_FIELDS.map((f): ColDef<MutualFund> => {
        const headerName = t(`funds.cols.${f}` as Parameters<typeof t>[0]);
        if (ETN_FK_FIELDS.has(f)) return makeFkCol(f, headerName, fkOpts[f] ?? []);
        const base: ColDef<MutualFund> = { field: f, headerName, editable: true, width: 150 };
        if (ETN_DATE_FIELDS.has(f))
          return { ...base, cellDataType: 'date', valueFormatter: (p: ValueFormatterParams<MutualFund>) => formatDate(p.value) };
        if (ETN_BOOL_FIELDS.has(f)) return { ...base, cellDataType: 'boolean', width: 120 };
        if (ETN_TEXT_FIELDS.has(f)) return base;
        return { ...base, cellDataType: 'number' };
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, companies.data]);

  if (anyError) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ color: 'var(--color-error-text)', background: 'var(--color-error-bg)', padding: '8px 12px', borderRadius: 4 }}>
          {t('funds.errorPrefix', { message: anyError })}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>;
  }

  return (
    <LopCrudPage<MutualFund>
      resourceCode="funds"
      titleKey="funds.title"
      idField="fundId"
      getRows={getFunds}
      saveBatch={(added, modified, deletedIds) => saveBatchFunds(added, modified, deletedIds as number[])}
      columnDefs={columnDefs}
      newRowDefaults={newFundDefaults}
    />
  );
}
